import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type PopoverContentProps = React.ComponentProps<typeof PopoverContent>;

interface DeleteConfirmPopoverContentProps extends Omit<PopoverContentProps, 'title' | 'children'> {
  title: React.ReactNode;
  description: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  isLoading?: boolean;
}

export const DeleteConfirmPopoverContent = ({
  title,
  description,
  onCancel,
  onConfirm,
  cancelLabel,
  confirmLabel,
  isLoading,
  side = 'bottom',
  align = 'end',
  className,
  ...props
}: DeleteConfirmPopoverContentProps) => {
  const { t } = useTranslation();

  const titleId = React.useId();
  const descriptionId = React.useId();

  return (
    <PopoverContent
      side={side}
      align={align}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className={cn('w-64 space-y-3', className)}
      {...props}
    >
      <div className="space-y-1">
        <p id={titleId} className="text-sm font-semibold">
          {title}
        </p>
        <p id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost-neutral" size="sm" onClick={onCancel}>
          {cancelLabel ?? t('common.cancel')}
        </Button>

        <Button variant="destructive" size="sm" isLoading={isLoading} onClick={onConfirm}>
          {confirmLabel ?? t('common.delete')}
        </Button>
      </div>
    </PopoverContent>
  );
};
